import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { THEME } from '../../../../components-v2/Theme/theme';
import { CloseButton } from '../../../../components/CloseButton';
import { dataGridStyles } from '../../../../components/DataGrid/data-grid-styles';
import { Modal } from '../../../../components/Modal';
import { MultiEmployeeSelector } from '../../../../components/email/MultiEmployeeSelector';
import { baseStyleSheet } from '../../../../styles/styles';
import { styles } from './styles';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	editingGroup?: Api.IUserGroup | null;
	onGroupNameChange: (name: string) => void;
	onAutomationPermissionChange: (checked: boolean, permission: 'read' | 'write' | 'viewReporting') => void;
	onSurveyPermissionChange: (
		checked: boolean,
		permission: 'read' | 'write' | 'viewReporting',
		surveyType?: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey'
	) => void;
	selectedUsers: Api.IUser[];
	onUserSelectionChange: (users: Api.IUser[]) => void;
	onSaveGroup: () => void;
	isPending: boolean;
};

export const CreateUserGroupModal = ({
	isOpen,
	onClose,
	editingGroup,
	onGroupNameChange,
	onAutomationPermissionChange,
	onSurveyPermissionChange,
	onUserSelectionChange,
	onSaveGroup,
	selectedUsers,
	isPending,
}: Props) => {
	return (
		<Modal isOpen={isOpen} onRequestClose={onClose} shouldCloseOnOverlayClick={false}>
			<div className={css(styles.modal)}>
				<div className={css(styles.modalHeaderCloseButton)}>
					<CloseButton onClick={onClose} size={12} fillColor={THEME.colors.text.light} />
				</div>

				<div className={css(styles.modalContainer)}>
					<div className={css(styles.modalHeader)}>
						<h2 className={css(styles.modalTitle)}>{editingGroup?.id ? 'Edit User Group' : 'Create User Group'}</h2>
					</div>

					{editingGroup && (
						<>
							<div className={css(styles.formGroup)}>
								<label className={css(styles.formLabel)}>
									Group Name
									{editingGroup.isPredefined && (
										<span
											style={{
												marginLeft: 8,
												fontSize: 11,
												color: '#6b7280',
												fontWeight: 'normal',
											}}
										>
											(Built-in - cannot be renamed)
										</span>
									)}
								</label>
								<input
									type='text'
									className={css(baseStyleSheet.textFieldStandard, editingGroup.isPredefined && styles.disabledInput)}
									value={editingGroup.name}
									onChange={e => onGroupNameChange(e.target.value)}
									placeholder='Enter group name'
									disabled={editingGroup.isPredefined}
								/>
							</div>

							<div className={css(styles.permissionSection)}>
								<div className={css(styles.permissionTitle)}>Feature Permissions</div>
								<div className={css(styles.permissionMatrix)}>
									{/* Header row */}
									<div className={css(styles.matrixHeader)}>
										<div
											className={css(
												styles.matrixHeaderCell,
												styles.matrixHeaderCellFirst,
												dataGridStyles.tableHeaderCell
											)}
										>
											Feature
										</div>
										<div className={css(styles.matrixHeaderCell, dataGridStyles.tableHeaderCell)}>Read</div>
										<div className={css(styles.matrixHeaderCell, dataGridStyles.tableHeaderCell)}>Write</div>
										<div className={css(styles.matrixHeaderCell, dataGridStyles.tableHeaderCell)}>View Reporting</div>
									</div>

									{/* Automation row */}
									<div className={css(styles.matrixRow)}>
										<div className={css(styles.matrixRowCell, styles.matrixRowCellFirst)}>Automation</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='automation-read'
												checked={editingGroup.featurePermissions?.automation?.read || false}
												onCheckedChange={checked => onAutomationPermissionChange(Boolean(checked), 'read')}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='automation-write'
												checked={editingGroup.featurePermissions?.automation?.write || false}
												onCheckedChange={checked => onAutomationPermissionChange(Boolean(checked), 'write')}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='automation-viewReporting'
												checked={editingGroup.featurePermissions?.automation?.viewReporting || false}
												onCheckedChange={checked => onAutomationPermissionChange(Boolean(checked), 'viewReporting')}
											/>
										</div>
									</div>

									{/* Event Registration Survey row */}
									<div className={css(styles.matrixRow)}>
										<div className={css(styles.matrixRowCell, styles.matrixRowCellFirst)}>
											Event Registration Survey
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='event-survey-read'
												checked={
													editingGroup.featurePermissions?.survey?.eventRegistrationSurvey?.read ??
													editingGroup.featurePermissions?.survey?.read ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'read', 'eventRegistrationSurvey')
												}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='event-survey-write'
												checked={
													editingGroup.featurePermissions?.survey?.eventRegistrationSurvey?.write ??
													editingGroup.featurePermissions?.survey?.write ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'write', 'eventRegistrationSurvey')
												}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='event-survey-viewReporting'
												checked={
													editingGroup.featurePermissions?.survey?.eventRegistrationSurvey?.viewReporting ??
													editingGroup.featurePermissions?.survey?.viewReporting ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'viewReporting', 'eventRegistrationSurvey')
												}
											/>
										</div>
									</div>

									{/* Satisfaction Survey row */}
									<div className={css(styles.matrixRow)}>
										<div className={css(styles.matrixRowCell, styles.matrixRowCellFirst)}>Satisfaction Survey</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='satisfaction-survey-read'
												checked={
													editingGroup.featurePermissions?.survey?.satisfactionSurvey?.read ??
													editingGroup.featurePermissions?.survey?.read ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'read', 'satisfactionSurvey')
												}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='satisfaction-survey-write'
												checked={
													editingGroup.featurePermissions?.survey?.satisfactionSurvey?.write ??
													editingGroup.featurePermissions?.survey?.write ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'write', 'satisfactionSurvey')
												}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='satisfaction-survey-viewReporting'
												checked={
													editingGroup.featurePermissions?.survey?.satisfactionSurvey?.viewReporting ??
													editingGroup.featurePermissions?.survey?.viewReporting ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'viewReporting', 'satisfactionSurvey')
												}
											/>
										</div>
									</div>

									{/* Custom Survey row */}
									<div className={css(styles.matrixRow)}>
										<div className={css(styles.matrixRowCell, styles.matrixRowCellFirst)}>Custom Survey</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='custom-survey-read'
												checked={
													editingGroup.featurePermissions?.survey?.customSurvey?.read ??
													editingGroup.featurePermissions?.survey?.read ??
													false
												}
												onCheckedChange={checked => onSurveyPermissionChange(Boolean(checked), 'read', 'customSurvey')}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='custom-survey-write'
												checked={
													editingGroup.featurePermissions?.survey?.customSurvey?.write ??
													editingGroup.featurePermissions?.survey?.write ??
													false
												}
												onCheckedChange={checked => onSurveyPermissionChange(Boolean(checked), 'write', 'customSurvey')}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='custom-survey-viewReporting'
												checked={
													editingGroup.featurePermissions?.survey?.customSurvey?.viewReporting ??
													editingGroup.featurePermissions?.survey?.viewReporting ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'viewReporting', 'customSurvey')
												}
											/>
										</div>
									</div>

									{/* Donation Survey row */}
									<div className={css(styles.matrixRow)}>
										<div className={css(styles.matrixRowCell, styles.matrixRowCellFirst)}>Donation Survey</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='donation-survey-read'
												checked={
													editingGroup.featurePermissions?.survey?.donationSurvey?.read ??
													editingGroup.featurePermissions?.survey?.read ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'read', 'donationSurvey')
												}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='donation-survey-write'
												checked={
													editingGroup.featurePermissions?.survey?.donationSurvey?.write ??
													editingGroup.featurePermissions?.survey?.write ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'write', 'donationSurvey')
												}
											/>
										</div>
										<div className={css(styles.matrixRowCell)}>
											<Checkbox
												id='donation-survey-viewReporting'
												checked={
													editingGroup.featurePermissions?.survey?.donationSurvey?.viewReporting ??
													editingGroup.featurePermissions?.survey?.viewReporting ??
													false
												}
												onCheckedChange={checked =>
													onSurveyPermissionChange(Boolean(checked), 'viewReporting', 'donationSurvey')
												}
											/>
										</div>
									</div>
								</div>
							</div>

							{!editingGroup.isPredefined && (
								<div className={css(styles.permissionSection)}>
									<div className={css(styles.permissionTitle)}>Group Members</div>
									<MultiEmployeeSelector employees={selectedUsers} onEmployeesChange={onUserSelectionChange} />
								</div>
							)}

							<div className={css(styles.modalActions)}>
								<Button size='sm' onClick={onSaveGroup} disabled={isPending}>
									{isPending ? 'Saving...' : editingGroup.id ? 'Update' : 'Create'}
								</Button>
								<Button variant='secondary' size='sm' onClick={onClose} disabled={isPending}>
									Cancel
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</Modal>
	);
};
